'use client';

// import colors from '@mui/joy/colors';
import {
	extendTheme as extendJoyTheme,
	shouldSkipGeneratingVar as joyShouldSkipGeneratingVar,
} from '@mui/joy/styles';
import type { PaletteAppBar } from '@mui/material';
import { blue, blueGrey, cyan, grey, lightBlue } from '@mui/material/colors';
import {
	type Overlays,
	type PaletteAlert,
	// PaletteAppBar,
	type PaletteAvatar,
	type PaletteChip,
	PaletteColor,
	type PaletteColorChannel,
	type PaletteFilledInput,
	type PaletteLinearProgress,
	type PaletteSkeleton,
	type PaletteSlider,
	type PaletteSnackbarContent,
	type PaletteSpeedDialAction,
	type PaletteStepConnector,
	type PaletteStepContent,
	type PaletteSwitch,
	type PaletteTableCell,
	type PaletteTooltip,
	type Shadows,
	type Theme,
	type TypeAction,
	type ZIndex,
	createTheme,
	extendTheme,
	responsiveFontSizes,
	// eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
	// experimental_extendTheme as extendMuiTheme,
} from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import components from './mui-components';
// import blue from "@mui/material/colors/blue";
// import blueGrey from "@mui/material/colors/blueGrey";
// import lightBlue from "@mui/material/colors/lightBlue";

declare module '@mui/material/styles' {
	interface Palette {
		blueGrey: Palette['primary'];
		red: Palette['primary'];
		blue: Palette['primary'];

		cyan: Palette['primary'];

		lightBlue: Palette['primary'];
		orange: Palette['primary'];
		yellow: Palette['primary'];
		green: Palette['primary'];
		purple: Palette['primary'];
	}
}

// extends Joy theme to include tokens from Material UI
declare module '@mui/joy/styles' {
	interface Palette {
		secondary: PaletteColorChannel;
		error: PaletteColorChannel;
		dividerChannel: string;
		AppBar: PaletteAppBar;
		action: TypeAction;
		Alert: PaletteAlert;
		Avatar: PaletteAvatar;
		Chip: PaletteChip;
		FilledInput: PaletteFilledInput;
		LinearProgress: PaletteLinearProgress;
		Skeleton: PaletteSkeleton;
		Slider: PaletteSlider;
		SnackbarContent: PaletteSnackbarContent;
		SpeedDialAction: PaletteSpeedDialAction;
		StepConnector: PaletteStepConnector;
		StepContent: PaletteStepContent;
		Switch: PaletteSwitch;
		TableCell: PaletteTableCell;
		Tooltip: PaletteTooltip;
	}
	// type PalettePrimary = PaletteColor;
	// type PaletteInfo = PaletteColor;
	// type PaletteSuccess = PaletteColor;
	// type PaletteWarning = PaletteColor;
	// type PaletteCommon = CommonColors;
	// type PaletteText = TypeText;
	// type PaletteBackground = TypeBackground;

	interface ThemeVars {
		// attach to Joy UI `theme.vars`
		shadows: Shadows;
		overlays: Overlays;
		zIndex: ZIndex;
	}
}

const { palette: createThemePalette } = createTheme();

const { augmentColor } = createThemePalette;

const palette = {
	layout: {
		background: '#f2f3f3',
	},
	primary: {
		main: blueGrey[700],
	},
	neutral: {
		main: grey[500],
	},
	grey,
	blueGrey: augmentColor({ color: blueGrey }),
	lightBlue: augmentColor({ color: lightBlue }),
	blue: augmentColor({ color: blue }),
	cyan: augmentColor({ color: cyan }),
};

// Note: you can't put `joyTheme` inside Material UI's `extendMuiTheme(joyTheme)`
// because some of the values in the Joy UI theme refers to CSS variables and
// not raw colors.
const muiTheme = extendTheme({
	colorSchemes: {
		light: {
			palette,
		},
		dark: {
			palette,
		},
	},
	components: components,
});

const BaseColorScheme = {
	palette: {
		primary: {
			...blueGrey,
			solidColor: 'var(--mui-palette-primary-contrastText)',
			solidBg: 'var(--mui-palette-primary-main)',
			solidHoverBg: 'var(--mui-palette-primary-dark)',
			plainColor: 'var(--mui-palette-primary-main)',
			plainHoverBg:
				'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
			plainActiveBg: 'rgba(var(--mui-palette-primary-mainChannel) / 0.3)',
			outlinedBorder: 'rgba(var(--mui-palette-primary-mainChannel) / 0.5)',
			outlinedColor: 'var(--mui-palette-primary-main)',
			outlinedHoverBg:
				'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
			outlinedHoverBorder: 'var(--mui-palette-primary-main)',
			outlinedActiveBg: 'rgba(var(--mui-palette-primary-mainChannel) / 0.3)',
		},
		neutral: {
			...grey,
		},
		// Do the same for the `danger`, `info`, `success`, and `warning` palettes,
		divider: 'var(--mui-palette-divider)',
		text: {
			tertiary: 'rgba(0 0 0 / 0.56)',
		},
	},
};

// export const roboto = Roboto({
//   weight: ['300', '400', '500', '700'],
//   subsets: ['latin'],
//   display: 'swap',
//   fallback: ['Helvetica', 'Arial', 'sans-serif'],
// });

// const muiTheme = createTheme({
//   palette,
// });
const { unstable_sxConfig: muiSxConfig, ...muiTheme2 }: Theme =
	responsiveFontSizes(muiTheme, {
		breakpoints: ['xs', 'sm', 'md', 'lg'],
	});

const { unstable_sxConfig: joySxConfig, ...joyTheme } = extendJoyTheme({
	// This is required to point to `var(--mui-*)` because we are using
	// `CssVarsProvider` from Material UI.
	cssVarPrefix: 'mui',
	components: {
		JoyTooltip: {
			styleOverrides: {
				root: ({ ownerState }) => {
					if (ownerState?.className?.includes('header')) {
						return {
							background: '#e6edf2',
							padding: '5px 8px',
							marginTop: '-12px !important',
							boxShadow:
								'0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
						};
					} else {
						return {};
					}
				},
			},
		},
	},
	colorSchemes: {
		light: BaseColorScheme,
		dark: BaseColorScheme,
		// Do the same for dark mode
		// dark: { ... }
	},
	fontFamily: {
		display: '"Roboto","Helvetica","Arial",sans-serif',
		body: '"Roboto","Helvetica","Arial",sans-serif',
	},
	shadow: {
		xs: `var(--mui-shadowRing), ${muiTheme.shadows[1]}`,
		sm: `var(--mui-shadowRing), ${muiTheme.shadows[2]}`,
		md: `var(--mui-shadowRing), ${muiTheme.shadows[4]}`,
		lg: `var(--mui-shadowRing), ${muiTheme.shadows[8]}`,
		xl: `var(--mui-shadowRing), ${muiTheme.shadows[12]}`,
	},
	// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
	shouldSkipGeneratingVar: (keys) => joyShouldSkipGeneratingVar(keys),
});
// any value that refer to CSS variables will have `--mui` prefix to match Material UI default prefix;

export const theme = deepmerge(joyTheme, muiTheme2) as unknown as ReturnType<
	typeof extendJoyTheme
>; // deepmerge(muiTheme2, joyTheme)

theme.unstable_sxConfig = {
	...muiSxConfig,
	...joySxConfig,
};

export type ThemeProps = {
	theme: typeof theme;
};
